import { useState } from "react";
import { useQueries, useQuery } from "@tanstack/react-query";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

import { messageFor } from "./format";
import { GlobalRulesSection } from "./GlobalRules";
import { RuleLibrary } from "./RuleLibrary";
import { byLens, createStrategy, libraryQuery, MARKETS, mapQuery, NAME_MAX, newStrategyBody, STRATEGIES_COPY as T, type Library, type Metric, type StrategyDraft, type StrategyMap } from "./strategies";
import { StrategyDefinitions } from "./StrategyCard";
import { StrategyMapEditor } from "./StrategyMapEditor";
import { FormError, MetricSelect, NoData, ResultLine, SectionHead, unitMark, useStrategyWrite, type Editor, type Result } from "./StrategyParts";

/**
 * The Strategies tab as an editor (C1 v1.15, Prototype v26 renderStrategies and its strategy editor, design calls A to R): the
 * page head with New strategy, one result line for every write, then the map, the rule library, the strategy definitions and the
 * metric definitions (design call R). Every write is C2's (c2-strategy-editing, C2 v1.17) and saves straight away; every write
 * control is left unmarked, so the shell's PermissionGate makes it inert for a view-only person and C2 refuses the write anyway.
 * Controls that change nothing (Retry, the toggles that show what is set, Cancel) carry `data-nonmutating`. Dialogs mount in an
 * anchor inside the tab, so they stay inside the gate.
 */
export function StrategiesTab({ areaName }: { areaName: string }) {
  const library = useQuery(libraryQuery());
  const maps = useQueries({ queries: MARKETS.map((m) => mapQuery(m)) });
  const [result, setResult] = useState<Result>(null);
  const [creating, setCreating] = useState(false);
  // State rather than a ref (the FactDetail pattern): a dialog's portal container has to be a rendered element when it mounts.
  const [anchor, setAnchor] = useState<HTMLDivElement | null>(null);
  const editor: Editor = { areaName, report: setResult, anchor };
  const heading = (withNew: boolean) => (
    <div className="page-head flex flex-wrap items-start justify-between gap-3">
      <div><h2 className="text-lg font-semibold text-ks-ink">{T.title}</h2><p className="text-sm text-ks-grey-1">{T.desc}</p></div>
      {withNew && <Button onClick={() => setCreating(true)}>{T.newStrategy}</Button>}
    </div>
  );
  const pending = library.isPending || maps.some((q) => q.isPending);
  const error = library.error ?? maps.find((q) => q.error)?.error;
  // Only a read with nothing to show replaces the editor, with `messageFor` its error. A failed refetch (after a write, or on
  // focus) keeps what was read, the result line and any open form, and says above them, with Retry, that the page may be out of
  // date: the person is looking at the strategies as last read, not at a page that failed.
  const missing = [library, ...maps].find((q) => q.data === undefined && q.error)?.error;
  const retry = () => { void library.refetch(); for (const q of maps) void q.refetch(); };
  const failed = (sentence: string) => (
    <Alert role="alert" variant="destructive" className="flex flex-wrap items-center justify-between gap-2">
      <AlertDescription>{sentence}</AlertDescription>
      <Button size="sm" variant="outline" data-nonmutating onClick={retry}>{T.retry}</Button>
    </Alert>
  );
  if (pending && !missing) {
    return <div className="flex flex-col gap-3">{heading(false)}<Card><CardContent className="flex flex-col gap-2 py-4">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-7 w-full" />)}</CardContent></Card></div>;
  }
  if (missing || !library.data) return <div className="flex flex-col gap-3">{heading(false)}{failed(messageFor(missing ?? error, areaName))}</div>;
  const loaded = maps.map((q) => q.data).filter((d): d is StrategyMap => d !== undefined);
  return (
    <div className="flex flex-col gap-6">
      {heading(true)}
      {error && failed(T.notRefreshed)}
      <ResultLine result={result} />
      {creating && <NewStrategyForm library={library.data} editor={editor} onClose={() => setCreating(false)} />}
      <GlobalRulesSection />
      <StrategyMapEditor maps={loaded} strategies={library.data.strategies} editor={editor} />
      <RuleLibrary library={library.data} editor={editor} />
      <StrategyDefinitions library={library.data} editor={editor} onNew={() => setCreating(true)} />
      <MetricDefinitions metrics={library.data.metrics} />
      <div ref={setAnchor} data-dialog-anchor="" />
    </div>
  );
}

/** v26's New strategy form: a name, the optimisation goal and its "as close to" target. Create-only: C2 refuses a taken name. */
export function NewStrategyForm({ library, editor, onClose }: { library: Library; editor: Editor; onClose: () => void }) {
  const [draft, setDraft] = useState<StrategyDraft>({ name: "", goal_metric: "", goal_target: "" });
  const [error, setError] = useState<string | null>(null);
  const create = useStrategyWrite(editor, createStrategy, (a) => T.done.created(a.name));
  const submit = () => {
    const checked = newStrategyBody(draft, library.strategies);
    if (!checked.ok) { setError(checked.error); return; }
    setError(null);
    create.mutate(checked.body, { onSuccess: onClose });
  };
  return (
    <Card data-form="new-strategy"><CardContent className="flex flex-col gap-2 py-4">
      <div className="font-semibold text-ks-ink">{T.create.title}</div>
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs text-ks-grey-1">{T.create.name}</span>
        {/* focused on open, which also scrolls the form into view: the empty state's New strategy sits below the whole map */}
        <Input autoFocus className="w-64" aria-label={T.create.name} placeholder={T.create.namePlaceholder} maxLength={NAME_MAX} value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
        <span className="text-xs text-ks-grey-1">{T.create.goal}</span>
        <MetricSelect metrics={library.metrics} value={draft.goal_metric} onChange={(goal_metric) => setDraft({ ...draft, goal_metric })} label={T.create.goal} placeholder={T.builder.pickMetric} />
        <span className="text-sm text-ks-grey-1">{T.create.asCloseTo}</span>
        <Input className="w-20" inputMode="decimal" aria-label={T.create.target} placeholder={T.create.targetPlaceholder} value={draft.goal_target} onChange={(e) => setDraft({ ...draft, goal_target: e.target.value })} />
        <span className="text-sm text-ks-grey-1">{unitMark(library.metrics, draft.goal_metric)}</span>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" onClick={submit} disabled={create.isPending}>{T.create.submit}</Button>
        <Button size="sm" variant="outline" data-nonmutating onClick={onClose}>{T.create.cancel}</Button>
      </div>
      <FormError error={error} />
    </CardContent></Card>
  );
}

/** The store's vocabulary last, grouped by lens (design call J), with C2's reason where the evaluator does not check a metric yet.
 * The metric's key is C2's, not a reader's word, so the table does not show it (review round 2). */
function MetricDefinitions({ metrics }: { metrics: Metric[] }) {
  return (
    <section className="flex flex-col gap-2" data-section="metrics">
      <SectionHead title={T.metrics.title} desc={T.metrics.desc} />
      <Card><CardContent className="py-2">
        <Table>
          <TableHeader><TableRow>{T.metrics.columns.map((c) => <TableHead key={c}>{c}</TableHead>)}</TableRow></TableHeader>
          <TableBody>
            {byLens(metrics).flatMap((g) => [
              ...(g.label ? [<TableRow key={`lens-${g.lens}`}><TableCell colSpan={3} className="pt-3 text-xs font-semibold tracking-wide text-ks-purple uppercase">{g.label}</TableCell></TableRow>] : []),
              ...g.items.map((m) => (
                <TableRow key={m.metric}>
                  <TableCell className="font-medium text-ks-ink">{m.label}</TableCell>
                  <TableCell>{T.unit[m.unit]}</TableCell>
                  <TableCell className="text-sm whitespace-normal text-ks-grey-1">{m.definition}<NoData reason={m.no_data} /></TableCell>
                </TableRow>
              )),
            ])}
          </TableBody>
        </Table>
      </CardContent></Card>
    </section>
  );
}
